import React, { useState, useMemo, useEffect, useRef } from 'react';
import {
  LoadingButton, Checkbox, Card, CardBody
} from '@one-thd/sui-atomic-components';
import { SweepstakesMarkdown } from '../../../utils/SweepstakesMarkdown';
import { windowExists } from '../../../../../common/utils/AccountUtils';
import { useFetchChallengesAccept, useResponsive } from '../../../../../hooks';
import { RenderGenericError } from '../../SweepstakesUtils';
import { ENDPOINTS, SWEEPSTAKES_OVERLAY_EVENTS, GenericErrorMessage } from '../../../../../core/Constants';
import { analyticsSweepstakesPostPageError } from '../../../../../common/utils/AnalyticsUtils';
import { OptInHeroHeader } from './OptInHeroHeader';

const RenderPrizeImage = ({ src }) => (
  <img
    src={src}
    alt="Sweepstakes prizes"
    className="sui-w-full"
    width={600}
    height={400}
    data-testid="sweepstakes-prizes-image"
  />
);

const RenderPrizes = ({ prizes }) => {
  if (!prizes?.length) return null;
  return (
    <div className="sui-space-y-4" data-testid="sweepstakes-prizes-list">
      {prizes.map((prize, index) => (
        <SweepstakesMarkdown
          key={`prize-${index}`}
          markdownText={prize}
          variant="prizesContent"
        />
      ))}
    </div>
  );
};

// Render components
const RenderCheckbox = ({ checked, onChange, text, testId }) => (
  <div className="sui-flex sui-items-center">
    <div className="sui-mr-2 sui-mt-1">
      <Checkbox
        checked={checked}
        onChange={(event) => onChange(event.target.checked)}
        data-testid={testId}
      />
    </div>
    <SweepstakesMarkdown markdownText={text} disableSpacing />
  </div>
);

const RenderFooterContent = ({
  acceptError,
  isLoadingAccept,
  isButtonDisabled,
  handleTakeSurvey,
  ctaTitle,
}) => (
  <div className="sui-space-y-2">
    {acceptError && (
      <div className="sui-py-3">
        <RenderGenericError
          refresh={handleTakeSurvey}
          testId="sweepstakes-accept-error"
        />
      </div>
    )}
    <LoadingButton
      variant="primary"
      fullWidth
      disabled={isButtonDisabled}
      loading={isLoadingAccept}
      onClick={handleTakeSurvey}
      data-testid="opt-in-take-survey-button"
    >
      {ctaTitle}
    </LoadingButton>
  </div>
);

export const OptInContent = (props) => {
  const { sweepstakesData, forceAcceptSuccess, handleOptIn, refresh } = props || {};
  const {
    preOptInDrawerBackgroundImage,
    preOptInDrawerHeaderLine1,
    preOptInDrawerHeaderLine2,
    preOptInDrawerDescription,
    preOptInDrawerPrizesHeading,
    optInPrizes = [],
    heroImage,
    preOptInDrawerCta,
    optInCheckboxes = [],
    requiredCheckboxIndices = [],
    challengeId,
    customerChallengeId,
    isV2,
    termsAndConditions,
  } = sweepstakesData || {};

  const [checkedStates, setCheckedStates] = useState({});

  const acceptChallengeInput = useMemo(() => ({
    id: challengeId,
    customerChallengeId,
    isV2
  }), [challengeId, customerChallengeId, isV2]);

  const { isLoadingAccept,
    hasErrorAccept,
    getAcceptChallenge } = useFetchChallengesAccept({ acceptChallengeInput });

  const acceptErrorFiredRef = useRef(false);
  useEffect(() => {
    if (!hasErrorAccept || acceptErrorFiredRef.current) return;
    acceptErrorFiredRef.current = true;
    analyticsSweepstakesPostPageError({
      type: 'system',
      displayType: 'inline',
      pageSection: SWEEPSTAKES_OVERLAY_EVENTS.OPT_IN,
      message: GenericErrorMessage,
      code: 'n/a',
    });
  }, [hasErrorAccept]);

  // Only validate required checkboxes (checkbox_1 is optional for email marketing)
  const areAllRequiredChecked = Array.isArray(requiredCheckboxIndices)
    ? requiredCheckboxIndices.every((index) => checkedStates[index])
    : optInCheckboxes?.every((_item, index) => checkedStates[index]);

  const handleCheckboxChange = (index, checked) => {
    setCheckedStates((prev) => ({
      ...prev,
      [index]: checked,
    }));
  };

  const handleTakeSurvey = async () => {
    if (areAllRequiredChecked) {
      let isAcceptSuccessful = false;

      if (forceAcceptSuccess) {
        // eslint-disable-next-line no-console
        console.log('OptInContent -- Bypassing acceptSweepstakes call! (for testing purposes)');
        isAcceptSuccessful = true;
      } else {
        try {
          const response = await getAcceptChallenge();
          isAcceptSuccessful = response?.data?.[ENDPOINTS.ACCEPT_CHALLENGES]
            ?.challenges?.[0]?.optedStatus === 'OPTED_IN';
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error('Accept challenge failed:', error);
          isAcceptSuccessful = false;
        }
      }

      if (isAcceptSuccessful && windowExists()) {
        handleOptIn();
        sessionStorage.setItem('checkbox_communication_consent', Boolean(checkedStates?.['0']));
        window.LIFE_CYCLE_EVENT_BUS.lifeCycle.trigger('loyalty-benefits.sweepstakes-accepted', {
          termsAccepted: true,
          associateConsent: true,
        });
        if (refresh) {
          try {
            await refresh();
          } catch (refreshError) {
            // eslint-disable-next-line no-console
            console.error('offeredChallenges refresh failed:', refreshError);
          }
        }
        window.LIFE_CYCLE_EVENT_BUS.lifeCycle.trigger('loyalty-benefits.open-sweepstakes-survey-drawer', {});
      }
    }
  };

  const { isMobile } = useResponsive();
  const isButtonDisabled = !areAllRequiredChecked || hasErrorAccept;

  const content = (
    <div data-testid="sweepstakes-opt-in-content">
      <OptInHeroHeader
        backgroundImage={preOptInDrawerBackgroundImage}
        headerLine1={preOptInDrawerHeaderLine1}
        headerLine2={preOptInDrawerHeaderLine2}
        description={preOptInDrawerDescription}
        prizesHeading={preOptInDrawerPrizesHeading}
        isMobile={isMobile}
      >
        <div className="sui-space-y-4">
          {isMobile ? (
            <Card disablePadding>
              {heroImage && <RenderPrizeImage src={heroImage} />}
              <CardBody padding>
                <RenderPrizes prizes={optInPrizes} />
              </CardBody>
            </Card>
          ) : (
            <div data-testid="sweepstakes-prizes-description">
              {heroImage && <RenderPrizeImage src={heroImage} />}
              <RenderPrizes prizes={optInPrizes} />
            </div>
          )}
          <div
            className={isMobile ? '' : 'sui-space-y-2'}
            data-testid="sweepstakes-opt-in-checkboxes-wrapper"
          >
            {isMobile ? (
              <Card>
                <CardBody>
                  <div className="sui-space-y-2" data-testid="sweepstakes-opt-in-checkboxes">
                    {optInCheckboxes.map((text, index) => (
                      <RenderCheckbox
                        key={`opt-in-checkbox-${index}`}
                        checked={!!checkedStates[index]}
                        onChange={(checked) => handleCheckboxChange(index, checked)}
                        text={text}
                        testId={`opt-in-checkbox-${index}`}
                      />
                    ))}
                  </div>
                </CardBody>
              </Card>
            ) : (
              <div className="sui-space-y-2" data-testid="sweepstakes-opt-in-checkboxes">
                {optInCheckboxes.map((text, index) => (
                  <RenderCheckbox
                    key={`opt-in-checkbox-${index}`}
                    checked={!!checkedStates[index]}
                    onChange={(checked) => handleCheckboxChange(index, checked)}
                    text={text}
                    testId={`opt-in-checkbox-${index}`}
                  />
                ))}
              </div>
            )}
          </div>
          {termsAndConditions && (
            <div className="sui-bg-subtle sui-p-3 sui-rounded" data-testid="sweepstakes-opt-in-terms">
              <SweepstakesMarkdown markdownText={termsAndConditions} disableSpacing />
            </div>
          )}
        </div>
      </OptInHeroHeader>
    </div>
  );

  const footer = (
    <RenderFooterContent
      acceptError={hasErrorAccept}
      isLoadingAccept={isLoadingAccept}
      isButtonDisabled={isButtonDisabled}
      handleTakeSurvey={handleTakeSurvey}
      ctaTitle={preOptInDrawerCta || 'Take Survey'}
    />
  );

  return { content, footer };
};
